import React, { useMemo, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import InvoicingDetailsForm from './../pages/Subscription/InvoicingDetailsForm';
import api from './../api/axios';

const PLAN_CONFIG = {
  basic: {
    name: 'Individual',
    eyebrow: 'For one user',
    monthly: 40,
    yearly: 400,
    icon: 'fa-user',
    description: 'Everything you need to run LinkedIn outreach and Agent Mode from one account.',
    features: ['1 user license', 'Campaign automation', 'Lead & message workspace', 'Agent Mode & approvals'],
  },
  team: {
    name: 'Team',
    eyebrow: 'For growing teams',
    monthly: 80,
    yearly: 800,
    icon: 'fa-users',
    description: 'Collaborate with multiple users and scale outreach from one organization.',
    features: ['Flexible user licenses', 'Team workspace', 'Shared integrations & CRM context', 'Central billing & administration'],
    recommended: true,
  },
};

const StepIndicator = ({ currentStep, onStep }) => {
  const steps = [
    { id: 1, label: 'Plan' },
    { id: 2, label: 'Billing details' },
    { id: 3, label: 'Review' },
  ];
  return (
    <div className="mb-7">
      <div className="flex items-center">
        {steps.map((item, index) => {
          const completed = currentStep > item.id;
          const active = currentStep === item.id;
          const canOpen = item.id <= currentStep;
          return (
            <React.Fragment key={item.id}>
              <button type="button" disabled={!canOpen} onClick={() => canOpen && onStep(item.id)} className="group flex min-w-0 items-center gap-2 disabled:cursor-default">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset transition ${completed ? 'bg-emerald-600 text-white ring-emerald-600' : active ? 'bg-blue-600 text-white ring-blue-600' : 'bg-white text-gray-400 ring-gray-300'}`}>
                  {completed ? <i className="fas fa-check" /> : item.id}
                </span>
                <span className={`hidden sm:block text-sm font-medium ${active ? 'text-gray-950' : completed ? 'text-gray-700' : 'text-gray-400'}`}>{item.label}</span>
              </button>
              {index < steps.length - 1 && <div className={`mx-3 h-px flex-1 ${currentStep > item.id ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const BillingToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-xl bg-gray-100 p-1 ring-1 ring-inset ring-gray-200">
    <button type="button" onClick={() => onChange('monthly')} className={`rounded-lg px-4 py-2 text-sm font-medium transition ${value === 'monthly' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Monthly</button>
    <button type="button" onClick={() => onChange('yearly')} className={`rounded-lg px-4 py-2 text-sm font-medium transition ${value === 'yearly' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
      Yearly <span className="ml-1 text-emerald-600">2 months free</span>
    </button>
  </div>
);

const SeatSelector = ({ value, onChange }) => {
  const setValue = (next) => onChange(Math.min(100, Math.max(1, Number(next) || 1)));
  return (
    <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-900">How many users?</div>
          <div className="mt-0.5 text-xs text-gray-500">You can change the number of licenses later.</div>
        </div>
        <div className="flex items-center rounded-lg border border-gray-300 bg-white p-1 shadow-sm">
          <button type="button" aria-label="Remove user" onClick={() => setValue(value - 1)} className="h-8 w-8 rounded-md text-gray-600 hover:bg-gray-100">−</button>
          <input aria-label="Number of users" type="number" min="1" max="100" value={value} onChange={(event) => setValue(event.target.value)} className="w-12 border-0 bg-transparent text-center text-sm font-semibold text-gray-950 focus:outline-none" />
          <button type="button" aria-label="Add user" onClick={() => setValue(value + 1)} className="h-8 w-8 rounded-md text-gray-600 hover:bg-gray-100">+</button>
        </div>
      </div>
    </div>
  );
};

const PlanCard = ({ type, billingCycle, seats, selected, onSelect, onSeats }) => {
  const plan = PLAN_CONFIG[type];
  const unitPrice = plan[billingCycle];
  const quantity = type === 'team' ? seats : 1;
  const total = unitPrice * quantity;
  return (
    <button type="button" onClick={() => onSelect(type)} className={`relative flex h-full w-full flex-col rounded-2xl border p-5 text-left transition-all ${selected ? 'border-blue-500 bg-blue-50/30 shadow-sm ring-2 ring-blue-100' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`}>
      {plan.recommended && <span className="absolute -top-3 right-4 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">Most flexible</span>}
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-950 text-white"><i className={`fas ${plan.icon}`} /></div>
        <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${selected ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-transparent'}`}><i className="fas fa-check text-[10px]" /></span>
      </div>
      <div className="mt-4">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{plan.eyebrow}</div>
        <h3 className="mt-1 text-xl font-semibold text-gray-950">{plan.name}</h3>
        <p className="mt-2 min-h-[40px] text-sm leading-5 text-gray-600">{plan.description}</p>
      </div>
      <div className="mt-5 flex items-baseline gap-1">
        <span className="text-3xl font-semibold tracking-tight text-gray-950">€{total}</span>
        <span className="text-sm text-gray-500">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
      </div>
      {type === 'team' && <div className="mt-1 text-xs text-gray-500">€{unitPrice} per user · {seats} {seats === 1 ? 'license' : 'licenses'}</div>}
      {type === 'team' && selected && <div onClick={(event) => event.stopPropagation()}><SeatSelector value={seats} onChange={onSeats} /></div>}
      <div className="mt-5 space-y-2.5">
        {plan.features.map((feature) => <div key={feature} className="flex items-center gap-2 text-sm text-gray-600"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><i className="fas fa-check text-[9px]" /></span>{feature}</div>)}
      </div>
    </button>
  );
};

const CheckoutForm = ({ email, activeSubscriptions = [], showBasicPlans = true }) => {
  const stripe = useStripe();
  const [step, setStep] = useState(1);
  const [selectedPlanType, setSelectedPlanType] = useState(showBasicPlans ? 'basic' : 'team');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [teamSeats, setTeamSeats] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoicingDetails, setInvoicingDetails] = useState({
    companyName: '', addressLine1: '', addressLine2: '', city: '', postalCode: '', state: '', country: '', vatNumber: '',
  });

  const selectedPlan = PLAN_CONFIG[selectedPlanType];
  const quantity = selectedPlanType === 'team' ? teamSeats : 1;
  const unitPrice = selectedPlan[billingCycle];
  const totalPrice = unitPrice * quantity;
  const planCode = `${selectedPlanType}_${billingCycle}`;
  const hasExistingSubscription = activeSubscriptions.length > 0;

  const priceLabel = useMemo(() => `€${totalPrice} / ${billingCycle === 'monthly' ? 'month' : 'year'}`, [totalPrice, billingCycle]);

  const continueToBilling = () => { setError(''); setStep(2); };
  const handleSaveInvoicingDetails = () => {
    if (!invoicingDetails.companyName || !invoicingDetails.addressLine1 || !invoicingDetails.city || !invoicingDetails.postalCode || !invoicingDetails.country) {
      setError('Please complete the required billing details before continuing.');
      return;
    }
    setError('');
    setStep(3);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!stripe || loading) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/subscription/create-checkout-session', {
        email,
        plan: planCode,
        invoicingDetails,
        quantity,
      });
      const sessionId = response.data?.sessionId;
      if (!sessionId) throw new Error('Checkout session was not created.');
      const result = await stripe.redirectToCheckout({ sessionId });
      if (result?.error) throw result.error;
    } catch (checkoutError) {
      console.error('Error processing subscription:', checkoutError);
      setError(checkoutError.response?.data?.message || checkoutError.message || 'Checkout could not be started. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <StepIndicator currentStep={step} onStep={setStep} />

      {error && <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"><i className="fas fa-circle-exclamation mt-0.5" /><span>{error}</span></div>}

      {step === 1 && (
        <div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-gray-950">Choose the plan that fits your workflow</h2>
              <p className="mt-1 text-sm text-gray-600">Start with one user or buy your Team licenses immediately. Billing is handled securely by Stripe.</p>
            </div>
            <BillingToggle value={billingCycle} onChange={setBillingCycle} />
          </div>

          <div className={`mt-6 grid gap-4 ${showBasicPlans ? 'md:grid-cols-2' : 'max-w-xl mx-auto'}`}>
            {showBasicPlans && <PlanCard type="basic" billingCycle={billingCycle} seats={teamSeats} selected={selectedPlanType === 'basic'} onSelect={setSelectedPlanType} onSeats={setTeamSeats} />}
            <PlanCard type="team" billingCycle={billingCycle} seats={teamSeats} selected={selectedPlanType === 'team'} onSelect={setSelectedPlanType} onSeats={setTeamSeats} />
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-500"><i className="fas fa-lock" /> Secure Stripe checkout · cancel or change later</div>
            <button type="button" onClick={continueToBilling} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700">
              Continue with {selectedPlan.name} <span className="opacity-80">· {priceLabel}</span> <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div><h2 className="text-xl font-semibold text-gray-950">Billing details</h2><p className="mt-1 text-sm text-gray-500">Used for invoices and tax calculation in Stripe.</p></div>
              <button type="button" onClick={() => setStep(1)} className="text-sm font-medium text-blue-700">Change plan</button>
            </div>
            <InvoicingDetailsForm invoicingDetails={invoicingDetails} setInvoicingDetails={setInvoicingDetails} handleSaveInvoicingDetails={handleSaveInvoicingDetails} error={error} />
          </section>
          <aside className="h-fit rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Your selection</div>
            <div className="mt-2 flex items-center justify-between gap-3"><strong className="text-gray-950">{selectedPlan.name}</strong><span className="text-sm text-gray-500">{billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}</span></div>
            {selectedPlanType === 'team' && <div className="mt-2 flex items-center justify-between text-sm text-gray-600"><span>Licenses</span><strong>{teamSeats}</strong></div>}
            <div className="mt-4 border-t border-gray-200 pt-4 flex items-baseline justify-between"><span className="text-sm text-gray-600">Total</span><strong className="text-xl text-gray-950">€{totalPrice}<span className="text-xs font-normal text-gray-500">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span></strong></div>
          </aside>
        </div>
      )}

      {step === 3 && (
        <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><i className="fas fa-check" /></span><div><h2 className="text-xl font-semibold text-gray-950">Review your subscription</h2><p className="text-sm text-gray-500">One final check before Stripe.</p></div></div>
            <div className="mt-6 divide-y divide-gray-100 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between gap-4 p-4"><div><div className="text-xs text-gray-400">Plan</div><div className="font-medium text-gray-950">{selectedPlan.name}</div></div><button type="button" onClick={() => setStep(1)} className="text-sm font-medium text-blue-700">Change</button></div>
              <div className="flex items-center justify-between gap-4 p-4"><div><div className="text-xs text-gray-400">Billing cycle</div><div className="font-medium text-gray-950">{billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}</div></div><span className="text-sm text-gray-500">{billingCycle === 'yearly' ? '2 months free' : 'Flexible monthly billing'}</span></div>
              {selectedPlanType === 'team' && <div className="flex items-center justify-between gap-4 p-4"><div><div className="text-xs text-gray-400">Team licenses</div><div className="font-medium text-gray-950">{teamSeats} users</div></div><button type="button" onClick={() => setStep(1)} className="text-sm font-medium text-blue-700">Edit</button></div>}
              <div className="flex items-start justify-between gap-4 p-4"><div><div className="text-xs text-gray-400">Invoice details</div><div className="mt-1 text-sm leading-5 text-gray-700">{invoicingDetails.companyName}<br />{invoicingDetails.addressLine1}{invoicingDetails.addressLine2 ? `, ${invoicingDetails.addressLine2}` : ''}<br />{invoicingDetails.postalCode} {invoicingDetails.city}<br />{invoicingDetails.country}</div></div><button type="button" onClick={() => setStep(2)} className="text-sm font-medium text-blue-700">Edit</button></div>
            </div>
            <div className="mt-5 rounded-xl bg-blue-50 p-4 text-sm text-blue-900"><i className="fas fa-circle-info mr-2" />After payment Leadgenflow activates your subscription automatically and opens your dashboard. No sign-out is required.</div>
          </section>

          <aside className="h-fit rounded-2xl bg-gray-950 p-6 text-white shadow-lg">
            <div className="text-sm text-gray-400">Due through Stripe</div>
            <div className="mt-2 text-4xl font-semibold tracking-tight">€{totalPrice}</div>
            <div className="mt-1 text-sm text-gray-400">per {billingCycle === 'monthly' ? 'month' : 'year'}{selectedPlanType === 'team' ? ` · ${teamSeats} licenses` : ''}</div>
            {selectedPlanType === 'team' && <div className="mt-4 rounded-lg bg-white/5 px-3 py-2 text-xs text-gray-300">€{unitPrice} per user / {billingCycle === 'monthly' ? 'month' : 'year'}</div>}
            <button type="button" onClick={handleSubmit} disabled={loading || !stripe || hasExistingSubscription} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-gray-950 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60">
              {loading ? <><i className="fas fa-spinner fa-spin" /> Opening Stripe…</> : <><i className="fas fa-lock" /> Continue to secure payment</>}
            </button>
            <div className="mt-4 text-center text-[11px] leading-4 text-gray-500">Payments are processed by Stripe. Your card or bank details are never stored by Leadgenflow.</div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CheckoutForm;
